/**
 * Normalized trial records.
 *
 * Shaped to be a faithful but *lossless-where-it-matters* projection of the
 * ClinicalTrials.gov v2 API. Fields we match on are normalized and typed; the
 * original API payload hash is retained so we can detect upstream changes without
 * storing the whole thing twice.
 */
import { z } from 'zod'
import {
  SexSchema,
  SiteStatusSchema,
  StudyTypeSchema,
  TrialPhaseSchema,
  TrialStatusSchema,
  isRecruitingStatus,
} from './vocab.js'
import { StructuredCriterionSchema } from './criteria.js'
import { GeoPointSchema, IsoDateSchema, IsoInstantSchema, idSchema } from './zod-util.js'
import type { ArmId, SiteId, TrialId } from './ids.js'

/**
 * A location running the trial.
 *
 * Site-level status is tracked separately from trial-level status because they
 * disagree constantly and the disagreement is operationally decisive: a trial
 * marked `RECRUITING` overall routinely has sites that are closed, full, or not
 * yet activated, and referring a patient to a closed site wastes everyone's time
 * and burns the referring clinician's willingness to try again.
 */
export const TrialSiteSchema = z.object({
  id: idSchema<SiteId>('site id'),
  facility: z.string().min(1),
  city: z.string().default(''),
  state: z.string().default(''),
  zip: z.string().default(''),
  country: z.string().default('United States'),
  geo: GeoPointSchema.optional(),
  status: SiteStatusSchema.default('UNKNOWN'),
  /** Public contact from the registry. Never a patient's contact — this is the study team's. */
  contactName: z.string().optional(),
  contactEmail: z.string().email().optional(),
  contactPhone: z.string().optional(),
  principalInvestigator: z.string().optional(),
  /** Remaining enrollment slots at this site, when the study team tells us. */
  slotsRemaining: z.number().int().nonnegative().optional(),
})
export type TrialSite = z.infer<typeof TrialSiteSchema>

/**
 * An arm or cohort. Modelled because enrollment caps and eligibility both bind at
 * the arm level in basket and umbrella trials — a biomarker-defined cohort can
 * close while the rest of the study keeps enrolling.
 */
export const TrialArmSchema = z.object({
  id: idSchema<ArmId>('arm id'),
  label: z.string().min(1),
  type: z.string().default(''),
  description: z.string().default(''),
  interventionNames: z.array(z.string()).default([]),
  /** Arm-specific criteria, on top of the study-wide list. */
  additionalCriterionIds: z.array(z.string()).default([]),
  slotsTotal: z.number().int().nonnegative().optional(),
  slotsFilled: z.number().int().nonnegative().optional(),
  open: z.boolean().default(true),
})
export type TrialArm = z.infer<typeof TrialArmSchema>

/**
 * The eligibility block, holding both the raw text and its parse.
 *
 * `rawText` is the contract with reality: the parse can be regenerated, improved,
 * or thrown away, but the source text is what the trial actually requires and what
 * gets shown when a clinician clicks "show me the original wording".
 */
export const EligibilitySchema = z.object({
  /** The verbatim free-text criteria blob from the registry. Always retained. */
  rawText: z.string().default(''),
  /** Structured criteria parsed from `rawText`. Empty until the parser has run. */
  criteria: z.array(StructuredCriterionSchema).default([]),

  /** Registry-structured demographic bounds, which are separate fields upstream. */
  minAgeYears: z.number().min(0).max(130).optional(),
  maxAgeYears: z.number().min(0).max(130).optional(),
  sex: SexSchema.default('ALL'),
  acceptsHealthyVolunteers: z.boolean().default(false),

  /** Fraction of `rawText` the parser structured. Drives the "needs review" badge. */
  parseCoverage: z.number().min(0).max(1).default(0),
})
export type Eligibility = z.infer<typeof EligibilitySchema>

/**
 * A normalized clinical trial.
 */
/**
 * A sponsor's own operating figures for a trial it runs: enrolment to date, per-site
 * enrolment, first patient in, and screening outcomes. Registries publish only the target,
 * so these come from the sponsor's trial-management system in a real deployment. In this
 * demo they are generated deterministically for the sponsor portfolios
 * (`web/scripts/build-matcher-index.ts`) so the enrolment views have believable numbers.
 */
export const TrialOperationsSchema = z.object({
  enrolled: z.number().int().nonnegative(),
  /** Enrolled participants per site id. Open sites missing from the map have enrolled nobody. */
  enrolledBySite: z.record(z.string(), z.number().int().nonnegative()),
  firstEnrolledDate: IsoDateSchema.optional(),
  screened: z.number().int().nonnegative(),
  screenFailed: z.number().int().nonnegative(),
  asOf: IsoDateSchema,
})
export type TrialOperations = z.infer<typeof TrialOperationsSchema>

export const TrialSchema = z.object({
  id: idSchema<TrialId>('trial id'),
  /** Where the record came from. Only `CTGOV` is implemented; the field exists so adding CTIS/ICTRP is not a migration. */
  source: z.enum(['CTGOV', 'CTIS', 'ICTRP', 'LOCAL']).default('CTGOV'),

  briefTitle: z.string().min(1),
  officialTitle: z.string().default(''),
  /** Registry's own lay summary, when present. Usually still too technical for patients. */
  briefSummary: z.string().default(''),
  detailedDescription: z.string().default(''),

  status: TrialStatusSchema,
  studyType: StudyTypeSchema.default('INTERVENTIONAL'),
  phases: z.array(TrialPhaseSchema).default([]),

  /** Condition names as authored by the sponsor — messy, unnormalized, and what patients search. */
  conditions: z.array(z.string()).default([]),
  /** Conditions normalized to codes by ingest. Used for matching; `conditions` is used for display. */
  conditionCodes: z.array(z.object({ system: z.string(), code: z.string(), display: z.string() })).default([]),
  interventions: z.array(z.object({ type: z.string().default(''), name: z.string() })).default([]),
  keywords: z.array(z.string()).default([]),
  /**
   * Coarse, patient-facing topic labels ("Heart failure", "Diabetes", …), distinct from
   * `keywords` (sponsor-authored, often too granular or jargon-heavy for a search chip)
   * and from `conditions` (verbatim sponsor text, not deduplicated across trials). Backfilled
   * at seed time from a condition-bucket map (see `web/scripts/seed.ts`) rather than parsed
   * live, so the mapping stays inspectable and cheap. Empty until backfilled.
   */
  tags: z.array(z.string()).default([]),

  leadSponsor: z.string().default(''),
  leadSponsorClass: z.enum(['INDUSTRY', 'NIH', 'FED', 'OTHER_GOV', 'NETWORK', 'AMBIG', 'OTHER', 'UNKNOWN']).default('UNKNOWN'),
  collaborators: z.array(z.string()).default([]),

  eligibility: EligibilitySchema,
  sites: z.array(TrialSiteSchema).default([]),
  arms: z.array(TrialArmSchema).default([]),

  enrollmentTarget: z.number().int().nonnegative().optional(),
  enrollmentActual: z.number().int().nonnegative().optional(),
  /** Sponsor-side operating figures; present only for trials in a demo sponsor portfolio. */
  operations: TrialOperationsSchema.optional(),

  startDate: IsoDateSchema.optional(),
  primaryCompletionDate: IsoDateSchema.optional(),
  lastUpdatePostedDate: IsoDateSchema.optional(),

  /** When we last pulled this record, and a hash of the upstream payload to detect drift. */
  ingestedAt: IsoInstantSchema,
  sourceHash: z.string().default(''),
})
export type Trial = z.infer<typeof TrialSchema>

/**
 * The trimmed projection sent to list views and to the retrieval index.
 *
 * Separate from `Trial` because a full trial with a parsed criteria array runs to
 * tens of kilobytes, and a patient's search results page would otherwise ship
 * megabytes to a phone to render twenty cards.
 */
export const TrialSummarySchema = TrialSchema.pick({
  id: true,
  briefTitle: true,
  status: true,
  phases: true,
  conditions: true,
  leadSponsor: true,
  tags: true,
}).extend({
  criteriaCount: z.number().int().nonnegative(),
  siteCount: z.number().int().nonnegative(),
  /** Distance in km from the requesting patient to the nearest recruiting site, when known. */
  nearestSiteKm: z.number().nonnegative().optional(),
  nearestSiteCity: z.string().optional(),
})
export type TrialSummary = z.infer<typeof TrialSummarySchema>

// ---------------------------------------------------------------------------
// Derived helpers
// ---------------------------------------------------------------------------

/** Sites that could actually take a patient today. */
export function openSites(trial: Trial): TrialSite[] {
  return trial.sites.filter(
    (s) => isRecruitingStatus(s.status as never) || s.status === 'NOT_YET_RECRUITING'
  )
}

/** Whether the trial as a whole can accept a referral. */
export function isTrialOpen(trial: Trial): boolean {
  return isRecruitingStatus(trial.status)
}

/**
 * Human-readable phase label, e.g. `Phase 1/2`.
 * Trials legitimately span phases, so this joins rather than picking one.
 */
export function phaseLabel(phases: readonly string[]): string {
  // No phase at all is how the registry records an observational study; "NA" is an
  // interventional study that is not a drug trial (device, programme, diet…).
  if (phases.length === 0) return 'Observational'
  const pretty = phases.map((p) =>
    p
      .replace('EARLY_PHASE1', 'Early Phase 1')
      .replace('PHASE1_PHASE2', 'Phase 1/2')
      .replace('PHASE2_PHASE3', 'Phase 2/3')
      .replace(/PHASE(\d)/, 'Phase $1')
      .replace(/^NA$/, 'No phase (not a drug trial)')
  )
  return [...new Set(pretty)].join(', ')
}

/**
 * Great-circle distance in kilometres between two points.
 *
 * Straight-line rather than driving distance on purpose: it needs no API call, is
 * deterministic in tests, and the decision it feeds ("is this site plausibly
 * reachable") is not sensitive to the difference. A real deployment should
 * upgrade this to travel time, which is what actually determines whether a
 * patient can make weekly visits.
 */
export function haversineKm(a: { lat: number; lng: number }, b: { lat: number; lng: number }): number {
  const R = 6371
  const toRad = (deg: number) => (deg * Math.PI) / 180
  const dLat = toRad(b.lat - a.lat)
  const dLng = toRad(b.lng - a.lng)
  const lat1 = toRad(a.lat)
  const lat2 = toRad(b.lat)
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(lat1) * Math.cos(lat2) * Math.sin(dLng / 2) ** 2
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(h)))
}

/** Nearest open site to a point, with its distance. `null` when nothing is open or geocoded. */
export function nearestOpenSite(
  trial: Trial,
  from: { lat: number; lng: number }
): { site: TrialSite; km: number } | null {
  let best: { site: TrialSite; km: number } | null = null
  for (const site of openSites(trial)) {
    if (!site.geo) continue
    const km = haversineKm(from, site.geo)
    if (!best || km < best.km) best = { site, km }
  }
  return best
}

/** The text blob fed to the retrieval index. Kept in `shared` so the indexer and the app agree. */
export function trialIndexText(trial: Trial): string {
  return [
    trial.briefTitle,
    trial.officialTitle,
    trial.conditions.join('; '),
    trial.tags.join('; '),
    trial.keywords.join('; '),
    trial.interventions.map((i) => i.name).join('; '),
    trial.briefSummary,
    trial.eligibility.rawText,
  ]
    .filter(Boolean)
    .join('\n')
}

/**
 * Registry text with ClinicalTrials.gov's markdown escapes removed ("HbA1c \>7%" → "HbA1c >7%").
 * The registry escapes markdown-significant characters in free text; nothing here renders
 * markdown, so the backslashes would otherwise reach the screen.
 */
export function cleanRegistryText(text: string): string {
  return text.replace(/\\([<>[\]*.~\-_#()!+`|{}])/g, '$1')
}

/**
 * A UCUM unit as a person reads it: annotations lose their braces ("{beats}/min" →
 * "beats/min", "10*3/uL" → "10³/µL" is out of scope). Display only — matching keeps the
 * canonical unit, since `units.ts` compares units as written.
 */
export function displayUnit(unit: string): string {
  return prettyUnitText(unit.replace(/\{([^{}]+)\}/g, '$1').replace(/^1$/, ''))
}

const SUPERSCRIPT: Record<string, string> = { '0': '⁰', '1': '¹', '2': '²', '3': '³', '4': '⁴', '5': '⁵', '6': '⁶', '7': '⁷', '8': '⁸', '9': '⁹' }

/** Rewrites UCUM spellings inside free text (`mm[Hg]`, `10*9/L`, `kg/m2`) the way people write them. */
export function prettyUnitText(text: string): string {
  return text
    .replace(/mm\[Hg\]/g, 'mmHg')
    .replace(/10\*(\d+)\/uL/g, (_, exp: string) => `×10${exp.replace(/\d/g, (d) => SUPERSCRIPT[d] ?? d)}/µL`)
    .replace(/10\*(\d+)\/L/g, (_, exp: string) => `×10${exp.replace(/\d/g, (d) => SUPERSCRIPT[d] ?? d)}/L`)
    .replace(/\bumol\//g, 'µmol/')
    .replace(/\bug\//g, 'µg/')
    .replace(/([/\d])m2\b/g, '$1m²')
}

/** Criterion text for display: the protocol's wording, with unit spellings and two known
 * registry-import artifacts (a footnote digit glued to "equation", "Type1") tidied. */
export function tidyCriterionText(text: string): string {
  return prettyUnitText(text.replace(/\bequation\d{1,2}\b/g, 'equation').replace(/\bType([12])\b/g, 'Type $1'))
}

/** The country the demo's patients live in. Real deployments read it from the patient's address. */
export const DEMO_PATIENT_COUNTRY = 'United States'

/**
 * Whether someone in `country` could take part: the study runs a site there, or has not
 * listed sites yet (so the study team is the one to ask). A study only running in Sweden is
 * not one to offer a patient in Ohio, however well they fit.
 */
export function offersSiteIn(trial: Trial, country: string): boolean {
  return trial.sites.length === 0 || trial.sites.some((s) => s.country === country)
}
